// Instance of the global rtcSession, used to flush the logs to the server via WS.
import { api_base_path, appversion } from '../services/Constants'
import { serializeError } from 'serialize-error'
import axios from 'axios'
import { env } from './EnvironmentVariables'
import ElectronIPC from './ElectronIPC'
import moment from 'moment'
import { getAppSerialId } from '../actions/AppSerialId'
import { defaultConsole } from '../DefaultConsole'
import { formatObjectNotation } from '../utils'

export interface LogContext {
    name: string
    [key: string]: any
}

export type LogLevel = 'silly' | 'verbose' | 'info' | 'checkpoint' | 'warning' | 'assert' | 'error' | 'critical'

export interface LogEntry {
    timestamp: Date
    context: LogContext
    scope: string
    level: LogLevel
    message: string
    vars?: { [key: string]: any }
}

export interface EventItem {
    timestamp: Date
    page: string
    item?: string
    action: string
}

export const LOG_LEVEL_TABEL: Record<LogLevel, number> = {
    critical: 100,
    error: 200,
    assert: 300,
    checkpoint: 400,
    warning: 500,
    info: 600,
    verbose: 700,
    silly: 800,
}

export const LOG_LEVEL_DEFAULT = 400

export const LOG_LEVELS: Array<{ level: number; label: LogLevel }> = [
    { level: 100, label: 'critical' },
    { level: 200, label: 'error' },
    { level: 300, label: 'assert' },
    { level: 400, label: 'checkpoint' },
    { level: 500, label: 'warning' },
    { level: 600, label: 'info' },
    { level: 700, label: 'verbose' },
    { level: 800, label: 'silly' },
]

export class RootLogger {
    private authToken: string | null = null
    private log: LogEntry[] = []
    private events: EventItem[] = []

    constructor() {
        console.log(`Instantiating RootLogger.`)
        this.flushLogs = this.flushLogs.bind(this)

        // periodically flush log to the server. (rtcSession.sendWSMessage)
        setInterval(() => this.flushLogs(), 1000 * 60)
    }

    setAuthToken(authToken: string) {
        this.authToken = authToken
    }

    createChildLogger(context: LogContext): Logger {
        return new Logger(this.log, context)
    }

    event(data: { page: string; action: string; item?: string; data?: any }) {
        this.events.push({ timestamp: new Date(), ...data })
    }

    flushLogs() {
        return new Promise((resolve) => {
            if (this.log.length === 0 && this.events.length === 0) {
                return
            }

            // make a copy of tihs.log and this.events so that we can retry if request fails.
            try {
                let logCopy = JSON.parse(
                    JSON.stringify(this.log.filter((a) => a.level !== 'verbose' && a.level !== 'silly'))
                )
                let eventsCopy = JSON.parse(JSON.stringify(this.events))

                this.log.length = 0
                this.events.length = 0

                // async
                this.flushLogsRequest(logCopy, eventsCopy, 0)
            } catch (e) {
                console.error(e)
            }
        })
    }

    flushLogsRequest(logs: any, events: any, depth: number) {
        return new Promise((resolve) => {
            if (env.NO_TELEMETRY) {
                resolve(true)
                return
            }

            const headers: any = {
                'X-Client-Version': appversion,
            }
            if (this.authToken) {
                headers.Authorization = 'Bearer ' + this.authToken
            }

            axios
                .post(
                    `${api_base_path}/v1/app/telemetry`,
                    {
                        appSerialId: getAppSerialId(),
                        instanceId: env.INSTANCE_ID,
                        logs: logs,
                        events: events,
                    },
                    {
                        headers: headers,
                    }
                )
                .then(() => {
                    resolve(true)
                })
                .catch((e) => {
                    console.error(e)

                    if (depth < 3) {
                        console.error(`telemetry: error uploading telemetry (depth=${depth}). retrying after timeout.`)
                        setTimeout(() => {
                            this.flushLogsRequest(logs, events, depth + 1)
                        }, 3000)
                    } else {
                        console.error(`telemetry: error uploading telemetry (depth=${depth}). retry limit exceeded.`)
                    }
                    resolve(false)
                })
        })
    }
}

// NOTE: Make sure to use `defaultConsole` in the log handlers. `console.log`, `error`, etc may be
// overridden to call Logger.verbose, Logger.error, etc.

export class Logger {
    private log: LogEntry[]
    context: LogContext

    constructor(log: LogEntry[], context: LogContext) {
        this.log = log
        this.context = context
    }

    isSilly() {
        return env.LOG_LEVEL >= LOG_LEVEL_TABEL['silly']
    }
    isVerbose() {
        return env.LOG_LEVEL >= LOG_LEVEL_TABEL['verbose']
    }

    silly(scope: string, message: string, vars?: { [key: string]: any }) {
        // this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'silly', message, vars: vars })
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['silly'] || env.ENABLE_AUDIT_LOGGING) {
            const text = 'SILLY ' + this.formatLog(scope, message, vars)
            if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['silly']) {
                defaultConsole.log(text)
            }
            if (env.ENABLE_AUDIT_LOGGING) {
                ElectronIPC.writeToLogFileStream(text)
            }
        }
    }

    // for logging rerouted console logs to disk
    console(scope: string, ...args: any) {
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['verbose'] || env.ENABLE_AUDIT_LOGGING) {
            const text = 'VERBOSE ' + this.formatLog(scope, formatObjectNotation(...args))
            if (env.ENABLE_AUDIT_LOGGING) {
                ElectronIPC.writeToLogFileStream(text)
            }
        }
    }

    verbose(scope: string, message: string, vars?: { [key: string]: any }) {
        // this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'verbose', message, vars: vars })
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['verbose'] || env.ENABLE_AUDIT_LOGGING) {
            const text = 'VERBOSE ' + this.formatLog(scope, message, vars)
            if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['verbose']) {
                defaultConsole.log(text)
            }
            if (env.ENABLE_AUDIT_LOGGING) {
                ElectronIPC.writeToLogFileStream(text)
            }
        }
    }

    info(scope: string, message: string, vars?: { [key: string]: any }) {
        this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'info', message, vars: vars })
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['info'] || env.ENABLE_AUDIT_LOGGING) {
            const text = 'INFO ' + this.formatLog(scope, message, vars)
            if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['info']) {
                defaultConsole.log(text)
            }
            if (env.ENABLE_AUDIT_LOGGING) {
                ElectronIPC.writeToLogFileStream(text)
            }
        }
    }

    warning(scope: string, message: string, vars?: { [key: string]: any }) {
        this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'warning', message, vars: vars })
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['warning'] || env.ENABLE_AUDIT_LOGGING) {
            const text = 'WARNING ' + this.formatLog(scope, message, vars)
            if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['warning']) {
                defaultConsole.warn(text)
            }
            if (env.ENABLE_AUDIT_LOGGING) {
                ElectronIPC.writeToLogFileStream(text)
            }
        }
    }

    /*
     * This is a structured event/log for debuging a users
     */
    checkpoint(id: string, message?: string, vars?: { [key: string]: any }) {
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['checkpoint'] || env.ENABLE_AUDIT_LOGGING) {
            if (!message) {
                message = ''
            }

            this.log.push({
                timestamp: new Date(),
                context: this.context,
                scope: id,
                level: 'checkpoint',
                message,
                vars: vars,
            })
            const text = 'CHECKPOINT ' + this.formatLog(id, message, vars)
            defaultConsole.log(text)
            ElectronIPC.writeToLogFileStream(text)
        }
    }

    /**
     *
     * Used to log assertions. Emit this log if something happened that should be impossible
     * under _any circumstances_.
     *
     * @param scope
     * @param message
     * @param vars
     */
    assert(scope: string, message: string, vars?: { [key: string]: any }) {
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['assert'] || env.ENABLE_AUDIT_LOGGING) {
            if (vars) {
                if (vars.e) {
                    vars.e = serializeError(vars.e)
                }
                if (vars.err) {
                    vars.err = serializeError(vars.err)
                }
            }

            this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'assert', message, vars: vars })
            const text = 'ASSERTION ' + this.formatLog(scope, message, vars)
            defaultConsole.error(text)
            ElectronIPC.writeToLogFileStream(text)
        }
    }

    error(scope: string, message: string, vars?: { [key: string]: any }) {
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['error'] || env.ENABLE_AUDIT_LOGGING) {
            if (vars) {
                if (vars.e) {
                    vars.e = serializeError(vars.e)
                }
                if (vars.err) {
                    vars.err = serializeError(vars.err)
                }
            }

            this.log.push({ timestamp: new Date(), context: this.context, scope, level: 'error', message, vars: vars })
            const text = 'ERROR ' + this.formatLog(scope, message, vars)
            defaultConsole.error(text)
            ElectronIPC.writeToLogFileStream(text)
        }
    }

    /*
     * Used to log errors that are unrecoverable and will result in the
     * application crashing or a seriously degraded user experience.
     */
    critical(scope: string, message: string, vars?: { [key: string]: any }) {
        if (env.LOG_LEVEL >= LOG_LEVEL_TABEL['critical'] || env.ENABLE_AUDIT_LOGGING) {
            if (vars) {
                if (vars.e) {
                    vars.e = serializeError(vars.e)
                }
                if (vars.err) {
                    vars.err = serializeError(vars.err)
                }
            }

            this.log.push({
                timestamp: new Date(),
                context: this.context,
                scope,
                level: 'critical',
                message,
                vars: vars,
            })
            const text = 'CRITICAL ' + this.formatLog(scope, message, vars)
            defaultConsole.error(text)
            ElectronIPC.writeToLogFileStream(text)
        }
    }

    private formatLog(scope: string, message: string, vars?: { [key: string]: any }): string {
        if (vars) {
            return `${moment().format()} [${this.context.name}/${scope}] ${message}\n${this.formatVarsObject(vars)}`
        } else {
            return `${moment().format()} [${this.context.name}/${scope}] ${message}`
        }
    }

    private formatVarsObject(vars: { [key: string]: any }): string {
        const indent = '    '
        return Object.keys(vars)
            .map((k) => {
                try {
                    const r =
                        vars[k] === undefined
                            ? 'undefined'
                            : JSON.stringify(vars[k], null, '  ')
                                  .split('\n')
                                  .map((s, i) => (i === 0 ? s : indent + s))
                                  .join('\n')

                    return indent + k + ': ' + r
                } catch (e) {
                    return `<failed to stringify object: e=${JSON.stringify(serializeError(e))}>`
                }
            })
            .join('\n')
    }
}

export const logger = new RootLogger()
export const defaultLogger = logger.createChildLogger({ name: 'default' })
